/**
 * HTML Sanitization Utility
 *
 * Uses DOMPurify to sanitize HTML content and prevent XSS attacks.
 * This is critical for any user-generated content that contains HTML,
 * including rich text editor output and legacy HTML stored in Firestore.
 */

import DOMPurify from 'isomorphic-dompurify';

/**
 * Sanitize HTML content for safe rendering
 *
 * @param html - Raw HTML string to sanitize
 * @returns Sanitized HTML safe for rendering with dangerouslySetInnerHTML
 *
 * @example
 * // Sanitize user-generated rich text
 * const safeHtml = sanitizeHtml(userDescription);
 * <div dangerouslySetInnerHTML={{ __html: safeHtml }} />
 *
 * @example
 * // Sanitize search highlighting
 * const highlighted = highlightMatch(text, query);
 * const safeHtml = sanitizeHtml(highlighted);
 */
/** Base allowed tags for basic rich text */
const BASE_ALLOWED_TAGS = [
  // Paragraph and line breaks
  'p', 'br',

  // Text formatting
  'strong', 'em', 'u', 's', 'span',

  // Headings
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',

  // Lists
  'ul', 'ol', 'li',

  // Links
  'a',

  // Search highlighting
  'mark',
];

/** Extended tags that include images and block elements */
const EXTENDED_ALLOWED_TAGS = [
  ...BASE_ALLOWED_TAGS,

  // Images
  'img',

  // Block elements for richer content
  'div', 'blockquote', 'pre', 'code',

  // Horizontal rules (section dividers in guides)
  'hr',

  // Tables (for structured content)
  'table', 'thead', 'tbody', 'tr', 'th', 'td',
];

/** Base allowed attributes */
const BASE_ALLOWED_ATTR = [
  'href',     // For links
  'class',    // For styling (e.g., Tailwind classes on <mark>)
  'style',    // For inline styles (e.g., text colors from Lexical editor)
];

/** Extended attributes that include image attributes */
const EXTENDED_ALLOWED_ATTR = [
  ...BASE_ALLOWED_ATTR,
  'src',      // For images
  'alt',      // For image accessibility
  'width',    // For image sizing
  'height',   // For image sizing
  'title',    // For tooltips
  'target',   // For link behavior
  'rel',      // For link security (noopener noreferrer)
  'id',       // For heading anchors (jump-to links in guides)
];

export function sanitizeHtml(html: string): string {
  if (!html) return '';

  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: BASE_ALLOWED_TAGS,
    ALLOWED_ATTR: BASE_ALLOWED_ATTR,

    // Additional security options
    ALLOW_DATA_ATTR: false,              // Block data-* attributes
    ALLOW_UNKNOWN_PROTOCOLS: false,      // Block javascript:, data:, etc.
    SAFE_FOR_TEMPLATES: true,            // Extra protection for template injection

    // URL sanitization for links
    ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
  });
}

/**
 * Sanitize HTML content with extended support for images and rich content
 *
 * Use this for rich announcements and other content that may contain images.
 * More permissive than sanitizeHtml but still secure.
 *
 * @param html - Raw HTML string to sanitize
 * @returns Sanitized HTML safe for rendering with dangerouslySetInnerHTML
 */
export function sanitizeRichHtml(html: string): string {
  if (!html) return '';

  // Add hook to force external links to open in new tab (skip anchor links)
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName === 'A') {
      const href = node.getAttribute('href') || '';
      if (!href.startsWith('#')) {
        node.setAttribute('target', '_blank');
        node.setAttribute('rel', 'noopener noreferrer');
      }
    }
  });

  const sanitized = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: EXTENDED_ALLOWED_TAGS,
    ALLOWED_ATTR: EXTENDED_ALLOWED_ATTR,

    // Additional security options
    ALLOW_DATA_ATTR: false,              // Block data-* attributes
    ALLOW_UNKNOWN_PROTOCOLS: false,      // Block javascript:, data:, etc.
    SAFE_FOR_TEMPLATES: true,            // Extra protection for template injection

    // URL sanitization for links and images (allows https for image sources)
    ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
  });

  // Remove the hook to avoid affecting other sanitize calls
  DOMPurify.removeHook('afterSanitizeAttributes');

  return sanitized;
}

/**
 * Escape HTML entities in plain text
 *
 * Use this when you need to display user input as plain text
 * (not as HTML). This prevents any HTML from being interpreted.
 *
 * @param text - Plain text that may contain HTML characters
 * @returns Escaped text safe for display
 *
 * @example
 * const userInput = '<script>alert("xss")</script>';
 * const escaped = escapeHtml(userInput);
 * // Result: '&lt;script&gt;alert("xss")&lt;/script&gt;'
 */
export function escapeHtml(text: string): string {
  if (!text) return '';

  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Strip all HTML tags from a string
 *
 * Use this when you need pure text content without any HTML markup.
 * Useful for PDF generation, plain text search, etc.
 *
 * @param html - HTML string to strip
 * @returns Plain text without HTML tags
 *
 * @example
 * const html = '<p>Hello <strong>World</strong></p>';
 * const text = stripHtmlTags(html);
 * // Result: 'Hello World'
 */
export function stripHtmlTags(html: string): string {
  if (!html) return '';

  // First sanitize to prevent any script execution during parsing
  const safe = DOMPurify.sanitize(html, { ALLOWED_TAGS: [] });

  // Create a temporary div to parse HTML and extract text
  const div = document.createElement('div');
  div.innerHTML = safe;

  return div.textContent || div.innerText || '';
}
